import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/apiClient";
import { useFinanceAccountPaths } from "@/lib/financeRouteContext";
import type { FinanceCustomer } from "@/lib/financeTypes";
import { FinanceErrorBanner } from "@/lib/financeUi";
import { Plus } from "lucide-react";

type CustomersResponse = { customers?: FinanceCustomer[] };

type Draft = { name: string; email: string; phone: string };

const emptyDraft = (): Draft => ({ name: "", email: "", phone: "" });

export function FinanceAccountsPage() {
  const { detailPath } = useFinanceAccountPaths();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<FinanceCustomer[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    const res = await apiGet<CustomersResponse>("/api/finance/customers?limit=200");
    if (!res.ok) {
      setError(res.error || "Could not load accounts.");
      setRows([]);
    } else {
      setRows(res.data?.customers ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditId(null);
    setDraft(emptyDraft());
    setDialogOpen(true);
  };

  const openEdit = (c: FinanceCustomer) => {
    setEditId(c.id);
    setDraft({ name: c.name || "", email: c.email || "", phone: c.phone || "" });
    setDialogOpen(true);
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    const body = {
      name: draft.name.trim(),
      email: draft.email.trim() || null,
      phone: draft.phone.trim() || null,
    };
    const res = editId
      ? await apiPatch<FinanceCustomer>(`/api/finance/customers/${encodeURIComponent(editId)}`, body)
      : await apiPost<FinanceCustomer>("/api/finance/customers", body);
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed.");
      return;
    }
    setDialogOpen(false);
    setEditId(null);
    setDraft(emptyDraft());
    await load();
  };

  const onConfirmDelete = async () => {
    if (!deleteId) return;
    setError(null);
    const res = await apiDelete<{ ok?: boolean }>(`/api/finance/customers/${encodeURIComponent(deleteId)}`);
    if (!res.ok) {
      setError(res.error || "Delete failed.");
      return;
    }
    setDeleteId(null);
    setDeleteOpen(false);
    await load();
  };

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">Accounts</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Bill-to accounts for invoices. Add an account first, then create an invoice.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add account
        </button>
      </div>

      {error ? <FinanceErrorBanner message={error} /> : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
            <tr>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Phone</th>
              <th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  Loading accounts…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-0">
                  <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
                    <p className="font-medium text-slate-900 dark:text-white">No accounts yet</p>
                    <p className="max-w-md text-sm text-slate-600 dark:text-slate-400">
                      Add a bill-to account first, then create an invoice.
                    </p>
                    <button
                      type="button"
                      onClick={openCreate}
                      className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
                    >
                      Add your first account
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((c) => {
                const href = detailPath(c.id);
                return (
                  <tr key={c.id} className="border-b border-slate-100 dark:border-white/5">
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                      <Link to={href} className="text-indigo-600 hover:underline dark:text-indigo-300">
                        {c.name?.trim() || c.id}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{c.email?.trim() || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{c.phone?.trim() || "—"}</td>
                    <td className="space-x-1 px-4 py-3 text-right">
                      <Link
                        to={href}
                        className="rounded-md px-2 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10"
                      >
                        Open
                      </Link>
                      <button
                        type="button"
                        onClick={() => openEdit(c)}
                        className="rounded-md px-2 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteId(c.id);
                          setDeleteOpen(true);
                        }}
                        className="rounded-md px-2 py-1 text-sm font-medium text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/30"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        <Link to="/finance/sales/invoices" className="text-indigo-600 hover:underline dark:text-indigo-300">
          Sales invoices
        </Link>
      </p>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? "Edit account" : "New account"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Name</span>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="Acme Corp"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Email</span>
              <input
                type="email"
                value={draft.email}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                placeholder="billing@example.com"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Phone</span>
              <input
                value={draft.phone}
                onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                placeholder="Optional"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDialogOpen(false)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={saving || !draft.name.trim()}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : editId ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete account?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            This cannot be undone. Invoices referencing this account are not automatically changed.
          </p>
          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteId(null);
              }}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void onConfirmDelete()}
              className="rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-500"
            >
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
